import {
  FilterCondition,
  FilterRules,
  ILibFilters,
  ILibOption,
} from '@infologistics/frontend-libraries';

import BoxesService from '@services/boxes';
import {
  EnvelopeFieldDirection,
  EnvelopeFieldType,
  IEnvelopeField,
  IEnvelopeSelectColumn,
  IEnvelopeSelectValue,
  isNumericEnvelopeFieldType,
} from '@store/modules/boxes/types';
import { EnvelopeDirection } from '@const/consts';
import { BooleanOptions } from '@const/translations';
import {
  displayErrorNotification,
  formatSelectDisplayTemplate,
} from '@utils/utils';

const booleanOptions: ILibOption[] = [
  {
    label: BooleanOptions.true,
    value: 'true',
  },
  {
    label: BooleanOptions.false,
    value: 'false',
  },
];

const SELECT_SERVER_MIN_QUERY_LENGTH = 3;
const SELECT_SERVER_VALUES_LIMIT = 5;

export const getEnvelopeFieldFilterName = (field: IEnvelopeField): string =>
  field.systemKey;

export const getEnvelopeDateOnlyFilterNames = (
  fields: IEnvelopeField[]
): string[] =>
  fields
    .filter(({ type }) => type === EnvelopeFieldType.DATE)
    .map((field) => getEnvelopeFieldFilterName(field));

export const getFieldDirectionMatches = (
  field: IEnvelopeField,
  direction?: EnvelopeDirection | null
): boolean => {
  if (!direction) return true;
  if (field.envelopeDirection === EnvelopeFieldDirection.BOTH) return true;

  return field.envelopeDirection === String(direction);
};

export const getEnvelopeListFields = (
  fields: IEnvelopeField[],
  direction?: EnvelopeDirection | null
): IEnvelopeField[] =>
  fields.filter((field) => (
    field.isVisibleInListView &&
    getFieldDirectionMatches(field, direction)
  ));

export const getEnvelopeFilterFields = (
  fields: IEnvelopeField[],
  direction?: EnvelopeDirection | null
): IEnvelopeField[] =>
  getEnvelopeListFields(fields, direction).filter((field) => (
    field.isSearchable !== false && field.type !== EnvelopeFieldType.TABLE
  ));

const BASE_OPERATORS = {
  [FilterCondition.ALL]: FilterRules.ALL,
  [FilterCondition.IS_NULL]: FilterRules.ALL,
  [FilterCondition.IS_NOT_NULL]: FilterRules.ALL,
  [FilterCondition.EQ]: FilterRules.TEXT,
  [FilterCondition.NE]: FilterRules.TEXT,
  [FilterCondition.IN]: FilterRules.TAGS,
};

const TEXT_OPERATORS = {
  ...BASE_OPERATORS,
  [FilterCondition.CONTAINS]: FilterRules.TEXT,
  [FilterCondition.BEGIN_WITH]: FilterRules.TEXT,
  [FilterCondition.END_WITH]: FilterRules.TEXT,
};

const ORDERED_OPERATORS = {
  ...BASE_OPERATORS,
  [FilterCondition.EQ]: FilterRules.CURRENCY,
  [FilterCondition.NE]: FilterRules.CURRENCY,
  [FilterCondition.LT]: FilterRules.CURRENCY,
  [FilterCondition.GT]: FilterRules.CURRENCY,
  [FilterCondition.LE]: FilterRules.CURRENCY,
  [FilterCondition.GE]: FilterRules.CURRENCY,
};

const DATE_OPERATORS = {
  [FilterCondition.ALL]: FilterRules.ALL,
  [FilterCondition.IS_NULL]: FilterRules.ALL,
  [FilterCondition.IS_NOT_NULL]: FilterRules.ALL,
  [FilterCondition.EQ]: FilterRules.DATE,
  [FilterCondition.NE]: FilterRules.DATE,
  [FilterCondition.IN]: FilterRules.TAGS,
  [FilterCondition.LT]: FilterRules.DATE,
  [FilterCondition.GT]: FilterRules.DATE,
  [FilterCondition.LE]: FilterRules.DATE,
  [FilterCondition.GE]: FilterRules.DATE,
};

const DATETIME_OPERATORS = {
  ...DATE_OPERATORS,
  [FilterCondition.EQ]: FilterRules.DATETIME,
  [FilterCondition.NE]: FilterRules.DATETIME,
  [FilterCondition.LT]: FilterRules.DATETIME,
  [FilterCondition.GT]: FilterRules.DATETIME,
  [FilterCondition.LE]: FilterRules.DATETIME,
  [FilterCondition.GE]: FilterRules.DATETIME,
};

const BOOLEAN_OPERATORS = {
  [FilterCondition.ALL]: FilterRules.ALL,
  [FilterCondition.IS_NULL]: FilterRules.ALL,
  [FilterCondition.IS_NOT_NULL]: FilterRules.ALL,
  [FilterCondition.EQ]: FilterRules.SELECT,
  [FilterCondition.NE]: FilterRules.SELECT,
  [FilterCondition.IN]: FilterRules.TAGS,
};

const SELECT_OPERATORS = {
  [FilterCondition.ALL]: FilterRules.ALL,
  [FilterCondition.IS_NULL]: FilterRules.ALL,
  [FilterCondition.IS_NOT_NULL]: FilterRules.ALL,
  [FilterCondition.EQ]: FilterRules.HANDBOOK,
  [FilterCondition.NE]: FilterRules.HANDBOOK,
  [FilterCondition.IN]: FilterRules.HANDBOOKS,
};

interface ISelectFilterHandbook {
  key: string;
  value: string;
}

const getFieldRules = (field: IEnvelopeField): ILibFilters[string]['rules'] => {
  if (isNumericEnvelopeFieldType(field.type)) return ORDERED_OPERATORS;

  switch (field.type) {
    case EnvelopeFieldType.STRING:
      return TEXT_OPERATORS;
    case EnvelopeFieldType.DATE:
      return DATE_OPERATORS;
    case EnvelopeFieldType.DATETIME:
      return DATETIME_OPERATORS;
    case EnvelopeFieldType.BOOL:
      return BOOLEAN_OPERATORS;
    case EnvelopeFieldType.SELECT:
      return SELECT_OPERATORS;
    default:
      return BASE_OPERATORS;
  }
};

const getFieldOptions = (field: IEnvelopeField): ILibOption[] | undefined => {
  if (field.type === EnvelopeFieldType.BOOL) return booleanOptions;

  return undefined;
};

const getSearchableSelectColumns = (field: IEnvelopeField): IEnvelopeSelectColumn[] => (
  (field.selectColumns ?? []).filter(({ isSearchable }) => isSearchable !== false)
);

export const getEnvelopeSelectSearchPlaceholder = (
  field: IEnvelopeField,
  t: (key: string, options?: Record<string, string>) => unknown
): string => {
  const searchableFields = getSearchableSelectColumns(field)
    .map(({ name, systemKey }) => (name || systemKey).trim().toLowerCase())
    .filter(Boolean)
    .join(', ');

  const placeholder = searchableFields
    ? t('envelope:selectSearchPlaceholder', { fields: searchableFields })
    : t('envelope:selectSearchPlaceholderFallback');

  return String(placeholder);
};

const getSelectValueLabel = (field: IEnvelopeField, value: IEnvelopeSelectValue): string => {
  const label = formatSelectDisplayTemplate(field.displayTemplate, value.fields);

  return label || value.key || '';
};

const getSelectColumnValue = (value: IEnvelopeSelectValue, column: IEnvelopeSelectColumn): unknown => (
  value.fields[column.systemKey]
);

const getSelectSearchText = (field: IEnvelopeField, value: IEnvelopeSelectValue): string => (
  getSearchableSelectColumns(field)
    .map((column) => getSelectColumnValue(value, column))
    .filter((fieldValue) => fieldValue !== null && fieldValue !== undefined && fieldValue !== '')
    .join(' ')
    .toLowerCase()
);

const getSelectFilterHandbook = (
  field: IEnvelopeField,
  value: IEnvelopeSelectValue
): ISelectFilterHandbook => ({
  key: value.key ?? '',
  value: getSelectValueLabel(field, value),
});

const filterSelectValues = (
  field: IEnvelopeField,
  values: IEnvelopeSelectValue[],
  query = ''
): ISelectFilterHandbook[] => {
  const normalizedQuery = query.trim().toLowerCase();

  return values
    .filter((value) => (
      value.key &&
      (!normalizedQuery || getSelectSearchText(field, value).includes(normalizedQuery))
    ))
    .map((value) => getSelectFilterHandbook(field, value));
};

const isServerSideSelectField = (field: IEnvelopeField): boolean => (
  field.type === EnvelopeFieldType.SELECT && field.isClientSideProcessing === false
);

const getSelectFilterPromises = (boxOguid: string, field: IEnvelopeField) => ({
  getData: (value: string) => {
    const query = value.trim();

    if (isServerSideSelectField(field) && query.length < SELECT_SERVER_MIN_QUERY_LENGTH) {
      return Promise.resolve({ data: [] });
    }

    return BoxesService.getEnvelopeFieldSelectValues(boxOguid, field.systemKey, {
      maxValuesToReturn: isServerSideSelectField(field)
        ? SELECT_SERVER_VALUES_LIMIT
        : field.maxValuesToReturn ?? undefined,
      queryLike: isServerSideSelectField(field) ? query : undefined,
    })
      .then((response) => ({
        ...response,
        data: isServerSideSelectField(field)
          ? response.data
              .filter(({ key }) => key)
              .map((selectValue) => getSelectFilterHandbook(field, selectValue))
          : filterSelectValues(field, response.data, value),
      }));
  },
  getHandbook: (key: string) => {
    const query = key.trim();

    if (isServerSideSelectField(field) && query.length < SELECT_SERVER_MIN_QUERY_LENGTH) {
      return Promise.resolve({ data: { key, value: key } });
    }

    return BoxesService.getEnvelopeFieldSelectValues(boxOguid, field.systemKey, {
      maxValuesToReturn: isServerSideSelectField(field)
        ? SELECT_SERVER_VALUES_LIMIT
        : field.maxValuesToReturn ?? undefined,
      queryLike: isServerSideSelectField(field) ? query : undefined,
    })
      .then((response) => {
        const selectValue = response.data.find((value) => value.key === key);

        return {
          ...response,
          data: selectValue ? getSelectFilterHandbook(field, selectValue) : { key, value: key },
        };
      });
  },
});

const getFieldSuggestProps = (
  field: IEnvelopeField,
  boxOguid?: string
): Pick<ILibFilters[string], 'handleSuggestHandbookPromiseError' | 'showSuggest' | 'suggestHandbookPromises'> => {
  if (field.type !== EnvelopeFieldType.SELECT || !boxOguid) return {};

  return {
    handleSuggestHandbookPromiseError: displayErrorNotification,
    showSuggest: true,
    suggestHandbookPromises: getSelectFilterPromises(boxOguid, field),
  };
};

export const getEnvelopeFieldFilters = (
  fields: IEnvelopeField[],
  direction?: EnvelopeDirection | null,
  boxOguid?: string
): ILibFilters =>
  getEnvelopeFilterFields(fields, direction).reduce<ILibFilters>((acc, field) => {
    const name = getEnvelopeFieldFilterName(field);

    return {
      ...acc,
      [name]: {
        ...getFieldSuggestProps(field, boxOguid),
        condition: FilterCondition.ALL,
        isAdditional: true,
        name,
        options: getFieldOptions(field),
        rules: getFieldRules(field),
        translation: field.name,
      },
    };
  }, {});
